import React from "react";
import { computed, observable } from "mobx";
import { observer } from "mobx-react";
import {
  withRouter,
  RouteComponentProps,
  generatePath,
} from "react-router-dom";
import { FormSpy } from "react-final-form";
import { debounce } from "debounce";

import KeyboardBackspaceIcon from "@material-ui/icons/KeyboardBackspace";

import { Button } from "@material-ui/core";

import ContentResourcesQuickLinksStore from "stores/ContentResourcesQuickLinksStore";

import Page from "components/Layout/Page";
import LoadingButton from "components/LoadingButton";
import FormControl, {
  EditorFieldControl,
  TextFieldControl,
} from "components/FormControl";
import SubmitButtonControl, {
  CancelButtonControl,
} from "components/FormControl/SubmitButtonControl";
import Popup from "components/Popup";
import ConfirmationDialog from "components/ConfirmationDialog";
import PublishStatus from "components/PublishStatus";
import SimpleField from "components/SimpleField";

import { SNACKBAR_TYPES } from "components/Snackbar";

import {
  compose,
  fileSizeValidator,
  imageIsCorruptedValidator,
  imageTypesValidator,
  lengthValidator,
  urlValidator,
} from "utils/validators";

import { PUBLISH_STATUSES } from "constants/models";
import { ACCEPTED_IMAGE_TYPES } from "constants/image";

import ROUTES from "navigation/routes";

import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";

import {
  BackButtonWrapper,
  Container,
  ControlsContainer,
  ControlContainer,
  Column,
  GroupRow,
  Row,
  Divider,
  UploadPreviewFieldControl,
  ImagePreviewContainer,
  PublishStatusWrapper,
} from "./styles";

type LocationState = {
  editMode: boolean;
  readOnly?: boolean;
};

type Props = RouteComponentProps<
  { quickLinkId: string; id?: string },
  {},
  LocationState
> &
  SnackbarContextProps & {};

const MAX_FILE_SIZE_IN_MB = 10;

@observer
class QuickLinkDetails extends React.Component<Props> {
  @observable isPopupDirty: boolean = false;
  @observable closeConfirmationIsOpened: boolean = false;
  @observable updateConfirmationIsOpened: boolean = false;
  @observable onAccept: () => void = () => {};

  quillEditorRef = React.createRef();

  get locationState() {
    return this.props.location.state;
  }

  get isEditMode() {
    return this.locationState?.editMode;
  }

  get isReadOnlyMode() {
    return this.locationState?.readOnly;
  }

  get isNewQuickLink() {
    const { quickLinkId } = this.props.match.params;

    return quickLinkId === undefined;
  }

  get quickLinkId() {
    const { quickLinkId } = this.props.match.params;

    const parsedQuickLinkId = Number.parseInt(quickLinkId);
    return typeof parsedQuickLinkId === "number"
      ? parsedQuickLinkId
      : undefined;
  }

  get categoryId() {
    const { id } = this.props.match.params;

    const categoryId = id ? Number.parseInt(id) : undefined;
    return typeof categoryId === "number" ? categoryId : undefined;
  }

  get backClickInfo() {
    const { pathname } = this.props.location;

    if (this.categoryId) {
      return {
        path: generatePath(ROUTES.IN_APP_RESOURCES_CATEGORY_DETAILS, {
          id: this.categoryId,
        }),
        text: "Back to Category Details",
      };
    }

    switch (pathname) {
      case generatePath(ROUTES.IN_APP_TODAY_NUTRITION_QUICK_LINK_DETAILS, {
        quickLinkId: this.quickLinkId,
      }):
        return {
          path: ROUTES.IN_APP_TODAY_NUTRITION,
          text: "Back to Nutrition Details",
        };
      case generatePath(ROUTES.IN_APP_TODAY_QUICK_LINKS_LINK_DETAILS, {
        quickLinkId: this.quickLinkId,
      }):
        return {
          path: ROUTES.IN_APP_TODAY_QUICK_LINKS,
          text: "Back to Quick Links Details",
        };
      case generatePath(ROUTES.CONTENT_RESOURCES_QUICK_LINK_DETAILS, {
        quickLinkId: this.quickLinkId,
      }):
        return {
          path: ROUTES.CONTENT_RESOURCES_QUICK_LINKS,
          text: "Back to Quick Links",
        };
      default:
        return {
          path: ROUTES.CONTENT_RESOURCES_QUICK_LINKS,
          text: "Back to Quick Links",
        };
    }
  }

  @computed get displayValues(): any {
    const formData = ContentResourcesQuickLinksStore.details?.formData;

    return {
      ...formData,
    };
  }

  @computed get initialValues() {
    if (this.isNewQuickLink) {
      return {};
    } else {
      return {
        ...ContentResourcesQuickLinksStore.details?.formData,
      };
    }
  }

  @computed get quickLinkPublishStatus() {
    return ContentResourcesQuickLinksStore.details?.publishStatus;
  }

  async componentDidMount() {
    const { history } = this.props;

    if (this.isNewQuickLink) {
      history.replace({ state: { editMode: true } });
    } else {
      await ContentResourcesQuickLinksStore.fetchQuickLinkDetails(
        this.quickLinkId,
      );
    }
  }

  componentWillUnmount() {
    ContentResourcesQuickLinksStore.resetDetails();
  }

  formFileValidator = (acceptedTypes: string) => (value?: File) =>
    imageTypesValidator(value?.type ?? "", acceptedTypes);

  closeConfirmationPopup = () => {
    this.closeConfirmationIsOpened = false;
  };

  onCloseForm = (onAccept: () => void) => {
    if (this.isPopupDirty) {
      this.closeConfirmationIsOpened = true;
      this.onAccept = onAccept;
    } else {
      onAccept();
    }
  };

  onBackClick = () => {
    const onAccept = () => this.props.history.push(this.backClickInfo.path);

    if (this.isEditMode) {
      this.onCloseForm(onAccept);
    } else {
      onAccept();
    }
  };

  onCancelClick = (resetForm: () => void) => {
    if (this.isNewQuickLink) {
      this.onBackClick();
    } else {
      const onAccept = () => {
        this.exitEditMode();
        this.closeConfirmationPopup();
        this.resetIsPopupDirty();
        resetForm();
      };
      this.onCloseForm(onAccept);
    }
  };

  onEditClick = () => {
    const { history } = this.props;

    const path = generatePath(ROUTES.CONTENT_RESOURCES_QUICK_LINK_DETAILS, {
      quickLinkId: this.quickLinkId,
    });
    history.replace({ pathname: path, state: { editMode: true } });
  };

  exitEditMode = () => {
    const { history } = this.props;

    const path = generatePath(ROUTES.CONTENT_RESOURCES_QUICK_LINK_DETAILS, {
      quickLinkId: this.quickLinkId,
    });
    history.replace({ pathname: path, state: { editMode: false } });
  };

  onChangeIsPopupDirty = ({
    dirty,
    dirtyFields,
    initialValues,
    values,
  }: {
    dirty: boolean;
    dirtyFields: any;
    initialValues: any;
    values: any;
  }) => {
    if (!dirty) {
      this.isPopupDirty = dirty;
      return;
    }

    const onlyDescriptionIsDirty =
      dirtyFields.description && Object.keys(dirtyFields).length === 1;

    if (!onlyDescriptionIsDirty) {
      this.isPopupDirty = dirty;
      return;
    }

    const editor = this.quillEditorRef?.current?.getEditor();
    const editorHistoryLastRecordTimeExist =
      editor?.history?.lastRecorded !== undefined;
    const currentDelta = editor?.clipboard?.convert(values.description);
    const initialDelta = editor?.clipboard?.convert(initialValues.description);
    const diff = currentDelta?.diff(initialDelta);

    const descriptionIsDirty = editorHistoryLastRecordTimeExist
      ? editor?.history?.lastRecorded !== 0 && !!diff?.ops?.length
      : !!diff?.ops?.length;

    this.isPopupDirty = descriptionIsDirty;
  };

  resetIsPopupDirty = () => {
    this.isPopupDirty = false;
  };

  openUpdateConfirmation = () => {
    this.updateConfirmationIsOpened = true;
  };

  closeUpdateConfirmation = () => {
    this.updateConfirmationIsOpened = false;
  };

  confirmUpdate = (values: any) => () => {
    this.closeUpdateConfirmation();
    this.submit(values);
  };

  onSubmit = (values: any) => {
    const shouldShowUpdateConfirmation =
      this.quickLinkPublishStatus &&
      [
        PUBLISH_STATUSES.PUBLISHED,
        PUBLISH_STATUSES.SCHEDULE_FOR_REMOVE,
      ].includes(this.quickLinkPublishStatus);

    shouldShowUpdateConfirmation
      ? this.openUpdateConfirmation()
      : this.submit(values);
  };

  submit = async (values: any) => {
    const { setMessage, history } = this.props;
    const { file, id, title, description, url } = values;

    const data = {
      description: description?.trim(),
      title: title?.trim(),
      url: url?.trim(),
    };

    const payload = { file, id, data: JSON.stringify(data) };

    try {
      if (this.isNewQuickLink) {
        const id = await ContentResourcesQuickLinksStore.createQuickLink(
          payload,
        );

        if (id) {
          const path = generatePath(
            ROUTES.CONTENT_RESOURCES_QUICK_LINK_DETAILS,
            {
              quickLinkId: id,
            },
          );
          history.push({ pathname: path, state: { editMode: false } });
          ContentResourcesQuickLinksStore.fetchQuickLinkDetails(id);
        }
      } else {
        const payload = { ...data, id };
        await ContentResourcesQuickLinksStore.editQuickLinkInfo(payload);

        if (typeof file !== "string") {
          const payload = { file, id };
          await ContentResourcesQuickLinksStore.editQuickLinkImage(payload);
        }

        this.exitEditMode();
        ContentResourcesQuickLinksStore.fetchQuickLinkDetails(id);
      }
      this.resetIsPopupDirty();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  render() {
    return (
      <Page
        title="Quick Link Details"
        leftControls={
          this.quickLinkPublishStatus && (
            <PublishStatusWrapper>
              <PublishStatus status={this.quickLinkPublishStatus} />
            </PublishStatusWrapper>
          )
        }
        topControls={
          <BackButtonWrapper>
            <Button
              color="secondary"
              variant="text"
              startIcon={<KeyboardBackspaceIcon />}
              onClick={this.onBackClick}
            >
              {this.backClickInfo.text}
            </Button>
          </BackButtonWrapper>
        }
        isLoading={ContentResourcesQuickLinksStore.isQuickLinkLoading}
      >
        {this.closeConfirmationIsOpened && (
          <Popup onClose={this.closeConfirmationPopup} withoutExitButton>
            <ConfirmationDialog
              title="Discard Changes"
              text="Are you sure you would like to discard changes? All information will be lost."
              declineText="Cancel"
              acceptText="Discard"
              onDecline={this.closeConfirmationPopup}
              onAccept={this.onAccept}
            />
          </Popup>
        )}
        <FormControl
          onSubmit={this.onSubmit}
          initialValues={this.initialValues}
          keepDirtyOnReinitialize={this.isNewQuickLink}
        >
          {this.updateConfirmationIsOpened && (
            <FormSpy subscription={{ values: true }}>
              {({ values }) => (
                <Popup onClose={this.closeUpdateConfirmation} withoutExitButton>
                  <ConfirmationDialog
                    title="Save Changes to Production"
                    text="All changes made will be published to production. Are you sure you want to proceed?"
                    acceptText="Proceed"
                    declineText="Cancel"
                    onAccept={this.confirmUpdate(values)}
                    onDecline={this.closeUpdateConfirmation}
                  />
                </Popup>
              )}
            </FormSpy>
          )}
          <FormSpy
            subscription={{
              dirty: true,
              dirtyFields: true,
              initialValues: true,
              values: true,
            }}
            onChange={debounce(this.onChangeIsPopupDirty, 500)}
          />
          <Container>
            <ControlsContainer>
              {this.isEditMode ? (
                <>
                  <ControlContainer>
                    <CancelButtonControl
                      render={this.renderCancelButton}
                      onClick={this.onCancelClick}
                    />
                  </ControlContainer>
                  <ControlContainer>
                    <SubmitButtonControl render={this.renderButton} />
                  </ControlContainer>
                </>
              ) : (
                !this.isReadOnlyMode && (
                  <ControlContainer>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={this.onEditClick}
                      fullWidth
                    >
                      Edit
                    </Button>
                  </ControlContainer>
                )
              )}
            </ControlsContainer>

            <GroupRow>
              <Column width="50%">
                <Row>
                  {this.isEditMode ? (
                    <UploadPreviewFieldControl
                      variant="outlined"
                      margin="none"
                      name="file"
                      acceptedFiles={ACCEPTED_IMAGE_TYPES}
                      validate={compose(
                        this.formFileValidator(ACCEPTED_IMAGE_TYPES),
                        fileSizeValidator(MAX_FILE_SIZE_IN_MB),
                        imageIsCorruptedValidator,
                      )}
                      required
                    />
                  ) : (
                    <ImagePreviewContainer
                      image={this.displayValues?.imageUrl}
                    />
                  )}
                </Row>
              </Column>

              <Column>
                <Row>
                  <Column>
                    {this.isEditMode ? (
                      <TextFieldControl
                        variant="outlined"
                        margin="none"
                        name="title"
                        label="Title"
                        validate={lengthValidator(100)}
                        fullWidth
                        required
                      />
                    ) : (
                      <SimpleField
                        label="Title"
                        value={this.displayValues?.title}
                      />
                    )}
                  </Column>
                </Row>

                <Row>
                  <Column>
                    {this.isEditMode ? (
                      <TextFieldControl
                        variant="outlined"
                        margin="none"
                        name="url"
                        label="URL"
                        validate={urlValidator}
                        fullWidth
                      />
                    ) : (
                      <SimpleField
                        label="URL"
                        value={this.displayValues?.url}
                      />
                    )}
                  </Column>
                </Row>
              </Column>
            </GroupRow>

            <Divider />

            <GroupRow>
              <Column>
                <Row>
                  {this.isEditMode ? (
                    <EditorFieldControl
                      editorRef={this.quillEditorRef}
                      name="description"
                      title="Description"
                      theme="snow"
                      changeDebounce={0}
                      required
                    />
                  ) : (
                    <SimpleField
                      label="Description"
                      value={this.displayValues?.description}
                      isRichText
                    />
                  )}
                </Row>
              </Column>
            </GroupRow>
          </Container>
        </FormControl>
      </Page>
    );
  }

  renderButton = ({ onClick }: { onClick: () => void }) => (
    <LoadingButton
      onClick={onClick}
      variant="contained"
      color="primary"
      isLoading={ContentResourcesQuickLinksStore.isQuickLinkManaging}
      fullWidth
    >
      {this.isNewQuickLink ? "Add" : "Save"}
    </LoadingButton>
  );

  renderCancelButton = ({ onClick }: { onClick: () => void }) => (
    <Button variant="outlined" color="primary" onClick={onClick} fullWidth>
      Cancel
    </Button>
  );
}

export default withGlobalSnackbar(withRouter(QuickLinkDetails));
